import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import LandingPage from './components/LandingPage';
import Signup from './components/Signup';
import LoginModal from './components/LoginModal';
import DashboardLayout from './components/DashboardLayout';
import PrivacyPolicy from './components/legal/PrivacyPolicy';
import TermsConditions from './components/legal/TermsConditions';
import RefundPolicy from './components/legal/RefundPolicy';
import { pageview, trackLoginSuccess } from './gtag';
import './App.css';

function AnalyticsListener() {
  const location = useLocation();
  useEffect(() => {
    pageview(location.pathname + location.search);
  }, [location]);
  return null;
}

function AppRoutes() {
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!localStorage.getItem('token'));
  const [loginOpen, setLoginOpen] = useState(false);
  const navigate = useNavigate();

  // Always sync auth state with localStorage
  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('token');
      setIsAuthenticated(!!token);
    };
    checkAuth();
    window.addEventListener('storage', checkAuth);
    return () => window.removeEventListener('storage', checkAuth);
  }, []);

  // Cashfree (and other redirects) may hit /?tab=... — keep logged-in users in the dashboard
  useEffect(() => {
    if (!isAuthenticated) return;
    const params = new URLSearchParams(window.location.search);
    const onRoot = window.location.pathname === '/' || window.location.pathname === '';
    if (!onRoot) return;
    if (params.get('payment') === 'return' || params.get('tab') || params.get('order_id')) {
      navigate(`/dashboard?${params.toString()}`, { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleLogin = (token, userEmail) => {
    if (token) localStorage.setItem('token', token);
    setIsAuthenticated(true);
    setLoginOpen(false);
    trackLoginSuccess(userEmail);
    navigate('/dashboard');
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setIsAuthenticated(false);
    navigate('/');
  };

  return (
    <Routes>
      <Route
        path="/"
        element={
          <>
            <LandingPage onLoginClick={() => setLoginOpen(true)} />
            <LoginModal open={loginOpen} onLogin={handleLogin} onClose={() => setLoginOpen(false)} />
          </>
        }
      />
      <Route path="/signup" element={<Signup />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsConditions />} />
      <Route path="/terms-conditions" element={<TermsConditions />} />
      <Route path="/refund-policy" element={<RefundPolicy />} />
      <Route
        path="/dashboard"
        element={
          isAuthenticated ? (
            <DashboardLayout onLogout={handleLogout} />
          ) : (
            <Navigate to="/" replace />
          )
        }
      />
      <Route
        path="/branches"
        element={
          isAuthenticated ? (
            <Navigate to="/dashboard?tab=branches" replace />
          ) : (
            <Navigate to="/" replace />
          )
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <Router>
      <AnalyticsListener />
      <AppRoutes />
    </Router>
  );
}
